import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';
import { addDaysIso, todayIso } from '../../utils/formatters.js';

export function ConvertQuoteModal({ quote, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [orderDate, setOrderDate] = useState(todayIso());
  const [requiredDate, setRequiredDate] = useState(addDaysIso(14));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (quote) {
      setOrderDate(todayIso());
      setRequiredDate(addDaysIso(14));
      setError(null);
    }
  }, [quote]);

  const convert = useMutation({
    mutationFn: ({ id, body }) => salesApi.convertQuote(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales', 'quotes'] });
      queryClient.invalidateQueries({ queryKey: ['sales', 'orders'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    convert.mutate({
      id: quote.id,
      body: { orderDate, requiredDate },
    });
  }

  return (
    <Modal
      open={Boolean(quote)}
      onClose={onClose}
      title={t('sales.quotes.convert.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={convert.isPending}>
            {convert.isPending ? `${t('common.save')}…` : t('sales.quotes.action.convert')}
          </Button>
        </>
      }
    >
      {quote && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            <strong>{quote.quoteNumber}</strong> - {t('sales.quotes.convert.intro')}
          </p>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('sales.quotes.convert.orderDate')}</span>
              <input className="input" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field-label">{t('sales.quotes.convert.requiredDate')}</span>
              <input className="input" type="date" value={requiredDate} onChange={(e) => setRequiredDate(e.target.value)} required />
            </label>
          </div>
          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
